import React, { forwardRef, type MouseEvent } from 'react';
import { cn } from '@mentimeter/ragnar-tailwind-config';
import type { ButtonT } from './Button.types';
import { ERROR_STATE, SUCCESS_STATE } from './constants';
import { FelaButton } from './FelaButton';
import { ButtonContent } from './components';
import { buttonVariants } from './Button.variants';

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonT
>((props, ref) => {
  const {
    status,
    size,
    children,
    disabled,
    iconLeading,
    iconTrailing,
    variant,
    className,
    onClick,
    ...rest
  } = props;

  const hasStatus = Boolean(status);
  const isDisabled = disabled || hasStatus;
  const isLink = rest?.href !== undefined;
  const asLinkProp = rest?.as ?? 'a';
  const isButton = !isLink;

  const VariantMap =
    status === SUCCESS_STATE
      ? 'positive'
      : status === ERROR_STATE
        ? 'negative'
        : variant;

  const buttonVariantsClasses = buttonVariants({
    variant: VariantMap,
    size,
  });

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  return (
    <FelaButton
      ref={ref}
      className={cn(buttonVariantsClasses, className)}
      data-has-status={hasStatus}
      onClick={handleClick}
      {...(isButton && { type: 'button', disabled: isDisabled })}
      {...(isLink && { as: asLinkProp, 'aria-disabled': isDisabled })}
      {...rest}
    >
      <ButtonContent
        status={status}
        size={size}
        children={children}
        iconLeading={iconLeading}
        iconTrailing={iconTrailing}
      />
    </FelaButton>
  );
});
